import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useI18n } from '../../context/I18nContext';

export interface SeoHeadProps {
  title?: string;
  description?: string;
  canonicalPath?: string;
  ogImage?: string;
  schemaJson?: Record<string, unknown>;
}

export const SeoHead: React.FC<SeoHeadProps> = ({
  title,
  description,
  canonicalPath = '',
  ogImage = '/favicon.svg',
  schemaJson,
}) => {
  const { locale, t } = useI18n();

  const siteName = 'Kapitalletter';
  const defaultTitle = `${siteName} | Official Mobile Software Studio`;
  const defaultDescription = t.home.heroSubtitle;

  const fullTitle = title ? `${title} | ${siteName}` : defaultTitle;
  const fullDesc = description || defaultDescription;
  const canonicalUrl = `https://project-kapital-app-dev.web.app${canonicalPath}`;

  return (
    <Helmet>
      <html lang={locale} />
      <title>{fullTitle}</title>
      <meta name="description" content={fullDesc} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDesc} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={ogImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDesc} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured Data (JSON-LD) */}
      {schemaJson && (
        <script type="application/ld+json">
          {JSON.stringify(schemaJson)}
        </script>
      )}
    </Helmet>
  );
};
